import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle, AlertCircle } from 'lucide-react';
import { InstagramIcon, TikTokIcon, SnapchatIcon, TwitterIcon } from '../icons/SocialMediaIcons';
import { Card, Button } from '../ui';
import { socialMediaAPI } from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { handleApiError, showToast } from '../../utils/helpers';

const SocialMediaIntegrations = () => {
  const { isRTL } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [disconnecting, setDisconnecting] = useState(null);

  const platforms = [
    {
      id: 'instagram',
      name: 'Instagram',
      nameAr: 'انستقرام',
      Icon: InstagramIcon,
      color: 'from-pink-500 to-purple-600',
      bgColor: 'bg-gradient-to-r from-pink-50 to-purple-50',
      borderColor: 'border-pink-200',
      description: 'Connect to post directly to Instagram',
      descriptionAr: 'اربط لنشر البوستات مباشرة على الانستقرام',
      enabled: true
    },
    {
      id: 'tiktok',
      name: 'TikTok',
      nameAr: 'تيك توك',
      Icon: TikTokIcon,
      color: 'from-black to-teal-500',
      bgColor: 'bg-gradient-to-r from-gray-50 to-teal-50',
      borderColor: 'border-gray-300',
      description: 'Connect to share videos on TikTok',
      descriptionAr: 'اربط لمشاركة الفيديوهات على تيك توك',
      enabled: false
    },
    {
      id: 'snapchat',
      name: 'Snapchat',
      nameAr: 'سناب شات',
      Icon: SnapchatIcon,
      color: 'from-yellow-400 to-yellow-600',
      bgColor: 'bg-gradient-to-r from-yellow-50 to-yellow-100',
      borderColor: 'border-yellow-300',
      description: 'Connect to share content on Snapchat',
      descriptionAr: 'اربط لمشاركة المحتوى على سناب شات',
      enabled: false
    },
    {
      id: 'x',
      name: 'X (Twitter)',
      nameAr: 'إكس (تويتر)',
      Icon: TwitterIcon,
      color: 'from-black to-gray-700',
      bgColor: 'bg-gradient-to-r from-gray-50 to-gray-100',
      borderColor: 'border-gray-300',
      description: 'Connect to post tweets',
      descriptionAr: 'اربط لنشر التغريدات',
      enabled: false
    }
  ];

  useEffect(() => {
    fetchConnections();
  }, []);

  // Handle OAuth callback success/error from URL params
  useEffect(() => {
    const success = searchParams.get('success');
    const error = searchParams.get('error');
    if (success === 'instagram_connected') {
      showToast.success(isRTL ? 'تم ربط إنستقرام بنجاح' : 'Instagram connected successfully');
      setSearchParams({}, { replace: true });
      fetchConnections();
    } else if (error) {
      const msg = error === 'instagram_auth_failed' || error === 'invalid_state'
        ? (isRTL ? 'فشل تسجيل الدخول' : 'Login failed')
        : error === 'instagram_connection_failed'
          ? (isRTL ? 'فشل ربط إنستقرام' : 'Instagram connection failed')
          : (isRTL ? 'حدث خطأ' : 'An error occurred');
      showToast.error(msg);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams]);

  const fetchConnections = async () => {
    try {
      setLoading(true);
      const response = await socialMediaAPI.getConnections();
      setConnections(response.data.connections || []);
    } catch (error) {
      console.error('Failed to fetch connections:', error);
      showToast.error(handleApiError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async (platformId) => {
    try {
      if (platformId === 'instagram') {
        // Get Instagram auth URL
        const response = await socialMediaAPI.getInstagramAuthUrl();
        // Redirect to Instagram OAuth
        window.location.href = response.data.authUrl;
      } else {
        showToast.info(isRTL ? 'قريباً...' : 'Coming soon...');
      }
    } catch (error) {
      console.error('Failed to connect:', error);
      showToast.error(handleApiError(error));
    }
  };

  const handleDisconnect = async (platformId) => {
    const confirmMessage = isRTL 
      ? 'هل أنت متأكد من فك الربط؟ سيتم حذف جميع البيانات المرتبطة.' 
      : 'Are you sure you want to disconnect? All associated data will be removed.';
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setDisconnecting(platformId);
      await socialMediaAPI.disconnect(platformId);
      showToast.success(isRTL ? 'تم فك الربط بنجاح' : 'Disconnected successfully');
      await fetchConnections();
    } catch (error) {
      console.error('Failed to disconnect:', error);
      showToast.error(handleApiError(error));
    } finally {
      setDisconnecting(null);
    }
  };

  const isConnected = (platformId) => {
    return connections.some(c => c.platform === platformId && c.is_active);
  };

  const getConnection = (platformId) => {
    return connections.find(c => c.platform === platformId && c.is_active);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <Card className="p-6 bg-gradient-to-r from-sky-50 via-white to-purple-50 border-slate-200">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-gradient-to-r from-sky-500 to-purple-500">
            <InstagramIcon className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-extrabold text-slate-900 mb-2">
              {isRTL ? 'ربط منصات التواصل الاجتماعي' : 'Social Media Integrations'}
            </h2>
            <p className="text-slate-600 text-sm">
              {isRTL 
                ? 'اربط حساباتك على منصات التواصل الاجتماعي لنشر المحتوى مباشرة من النظام' 
                : 'Connect your social media accounts to publish content directly from the platform'}
            </p>
          </div>
        </div>
      </Card>

      {/* Platforms Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {platforms.map((platform) => {
          const Icon = platform.Icon;
          const connected = isConnected(platform.id);
          const connection = getConnection(platform.id);

          return (
            <Card 
              key={platform.id} 
              className={`p-6 ${platform.bgColor} border-2 ${
                connected ? 'border-green-300' : platform.borderColor
              } transition-all duration-200 hover:shadow-lg`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${platform.color} flex items-center justify-center shadow-lg`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">
                      {isRTL ? platform.nameAr : platform.name}
                    </h3>
                    {connected && connection && (
                      <p className="text-sm text-slate-600 flex items-center gap-1">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        @{connection.platform_username}
                      </p>
                    )}
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${
                  connected 
                    ? 'bg-green-100 text-green-700 border border-green-300' 
                    : 'bg-gray-100 text-gray-600 border border-gray-300'
                }`}>
                  {connected ? (
                    <>
                      <CheckCircle className="w-3 h-3" />
                      {isRTL ? 'متصل' : 'Connected'}
                    </>
                  ) : (
                    <>
                      <AlertCircle className="w-3 h-3" />
                      {isRTL ? 'غير متصل' : 'Not Connected'}
                    </>
                  )}
                </span>
              </div>

              <p className="text-sm text-slate-600 mb-4">
                {isRTL ? platform.descriptionAr : platform.description}
              </p>

              {connected ? (
                <Button
                  onClick={() => handleDisconnect(platform.id)}
                  disabled={disconnecting === platform.id}
                  loading={disconnecting === platform.id}
                  variant="outline"
                  className="w-full border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400"
                >
                  {isRTL ? 'فك الربط' : 'Disconnect'}
                </Button>
              ) : (
                <Button
                  onClick={() => handleConnect(platform.id)}
                  variant="primary"
                  className={`w-full ${
                    platform.enabled
                      ? `bg-gradient-to-r ${platform.color} hover:opacity-90 text-white shadow-lg`
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                  disabled={!platform.enabled}
                >
                  {isRTL ? 'ربط الحساب' : 'Connect Account'}
                  {!platform.enabled && (
                    <span className={`${isRTL ? 'mr-2' : 'ml-2'} text-xs`}>
                      ({isRTL ? 'قريباً' : 'Soon'})
                    </span>
                  )}
                </Button>
              )}

              {connected && connection && (
                <div className="mt-3 pt-3 border-t border-slate-200">
                  <p className="text-xs text-slate-500">
                    {isRTL ? 'تم الربط في' : 'Connected on'}{' '}
                    {new Date(connection.created_at).toLocaleDateString(
                      isRTL ? 'ar-SA' : 'en-US',
                      { year: 'numeric', month: 'long', day: 'numeric' }
                    )}
                  </p>
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* Info Card */}
      <Card className="p-4 bg-blue-50 border-blue-200">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900">
            <p className="font-semibold mb-1">
              {isRTL ? 'ملاحظة مهمة' : 'Important Note'}
            </p>
            <p className="text-blue-700">
              {isRTL
                ? 'لربط حسابك على Instagram، تحتاج إلى حساب أعمال (Business Account) أو حساب منشئ محتوى (Creator Account).'
                : 'To connect your Instagram account, you need either a Business Account or a Creator Account.'}
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default SocialMediaIntegrations;
